/**
 * Thin fetch wrapper for portal → parkify-pay-api calls.
 *
 * - Bearer token is injected from localStorage `parkify_portal_token`.
 * - 401 responses clear the cached token so the App falls back to SignIn.
 */

import type {
  CustomerProfile, ProfilePatch, Plate, ExtendResult, ExtendQuote,
  PaymentMethod, SavedCard, PortalSubscription, PortalPermit,
  CustomerChatMessage, CustomerChatResponse,
} from '@parkify/account'

// These account types now live in the shared @parkify/account package. Re-export
// them so existing portal importers that pulled them from '../api' keep working.
export type {
  Visit, CustomerProfile, ProfilePatch, Plate, ExtendResult, ExtendQuote,
  PaymentMethod, PortalZoneRef, SubscriptionStatus, PortalSubscription, PortalPermit,
} from '@parkify/account'
// Ask Avery types moved to the shared package too — re-exported below,
// alongside the sendCustomerChat/fileRefundRequest bodies that use them.

// Exported so the FindParking screen can pass it to the shared
// @parkify/find-parking browse component (which fetches the public
// /api/locations directory cross-origin from this same Worker).
// `api.parkify.com` — same host the consumer now uses. See the note in
// `parkify-pay/consumer/src/lib/api.ts` for why the registrable domain matters
// (session cookie) and for the #1074/#1075 DNS history. VITE_API_BASE still wins,
// so rolling back to the workers.dev host is a redeploy, not a code change.
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)
  ?? 'https://api.parkify.com'

const TOKEN_KEY = 'parkify_portal_token'
const EMAIL_KEY = 'parkify_portal_email'
const PHONE_KEY = 'parkify_portal_phone'

// ─── Safe storage ──────────────────────────────────────────────────────────────
// `window.localStorage` doesn't just throw on WRITE (private mode) — merely
// ACCESSING it throws a SecurityError on iOS Safari with "Block All Cookies"
// enabled and inside privacy-partitioned in-app webviews (Slack/email/iMessage
// link previews). getToken() runs during the App's very first render (App ->
// currentRoute), so an unguarded read there crashed the whole mount — and with
// no error boundary that white-screened the entire portal (blank sign-in page).
// Guard every access, and mirror to an in-memory store so a session still works
// for THIS visit when the disk store is unavailable. Same philosophy as the
// consumer app's lib/session.ts.
const memStore: Record<string, string> = {}

function safeGet(key: string): string | null {
  try {
    const v = localStorage.getItem(key)
    if (v !== null) return v
  } catch { /* storage blocked — fall through to the in-memory mirror */ }
  return key in memStore ? memStore[key]! : null
}

function safeSet(key: string, value: string): void {
  memStore[key] = value // always keep the in-memory copy so this visit works
  try { localStorage.setItem(key, value) } catch { /* persist unavailable — memory only */ }
}

function safeRemove(key: string): void {
  delete memStore[key]
  try { localStorage.removeItem(key) } catch { /* nothing to remove */ }
}

export function getToken(): string | null {
  return safeGet(TOKEN_KEY)
}

export function setSession(token: string, email: string): void {
  safeSet(TOKEN_KEY, token)
  safeSet(EMAIL_KEY, email)
  safeRemove(PHONE_KEY)
}

// Phone-first sign-in: store the token + the verified number (for display).
export function setPhoneSession(token: string, phone: string): void {
  safeSet(TOKEN_KEY, token)
  safeSet(PHONE_KEY, phone)
  safeRemove(EMAIL_KEY)
}

// Swap only the stored token — used after a re-minted session (verified phone
// change), preserving the cached email/phone identity display. The portal reads
// the token from storage on every request, so this alone keeps auth flowing.
export function setToken(token: string): void {
  safeSet(TOKEN_KEY, token)
}

export function getEmail(): string | null {
  return safeGet(EMAIL_KEY)
}

export function getPhone(): string | null {
  return safeGet(PHONE_KEY)
}

export function clearSession(): void {
  safeRemove(TOKEN_KEY)
  safeRemove(EMAIL_KEY)
  safeRemove(PHONE_KEY)
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  { authed }: { authed?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  if (authed) {
    const token = getToken()
    if (!token) throw new ApiError(401, 'Not signed in')
    headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })

  if (res.status === 401 && authed) {
    clearSession()
    throw new ApiError(401, 'Session expired')
  }

  // Worker uses { error: "..." } for non-2xx; surface that as the thrown message.
  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json() as { error?: string }
      if (body.error) detail = body.error
    } catch { /* keep statusText */ }
    throw new ApiError(res.status, detail)
  }

  return res.json() as Promise<T>
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function requestMagicLink(email: string): Promise<{ ok: true }> {
  return request('/api/portal/auth/request-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function verifyMagicLink(token: string): Promise<{ token: string; email: string }> {
  return request('/api/portal/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

// Phone-first sign-in (Twilio Verify) — same endpoints the consumer checkout
// uses. start texts a code; check verifies it and returns a portal JWT.
// `turnstileToken` rides along when the build ships a Turnstile sitekey
// (SMS-pumping defense on the send-code screen). DARK for now: the server
// ignores it on /phone/start until verifyTurnstile is wired there (#1b-ii step
// 3); the #834 rate limiter is the live floor. Dropped from the body when
// absent (JSON.stringify omits undefined).
export async function startPhoneOtp(phone: string, turnstileToken?: string): Promise<{ ok: boolean }> {
  return request('/api/portal/auth/phone/start', {
    method: 'POST',
    body: JSON.stringify({ phone, turnstileToken: turnstileToken || undefined }),
  })
}

export async function checkPhoneOtp(phone: string, code: string): Promise<{ token: string; phone: string }> {
  return request('/api/portal/auth/phone/check', {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
  })
}

// ─── Profile ─────────────────────────────────────────────────────────────────

export async function fetchMe(): Promise<CustomerProfile> {
  return request<CustomerProfile>('/api/portal/me', { method: 'GET' }, { authed: true })
}

// ─── Profile editing ─────────────────────────────────────────────────────────
// PATCH semantics — only fields you pass are updated. Email is immutable
// (it's the identity from the magic-link JWT). Server returns the full
// updated profile (without visits — those don't change on a profile edit).

/** Updates only the fields in `patch`. Returns the updated profile shape.
 *  NOTE: `phone` is no longer changed here — it's an SMS-verified flow (see
 *  startPhoneChange / verifyPhoneChange). The server ignores phone on this PATCH. */
export async function patchProfile(patch: ProfilePatch): Promise<Omit<CustomerProfile, 'visits'>> {
  return request('/api/portal/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }, { authed: true })
}

// ─── Phone-number change (SMS-verified) ───────────────────────────────────────
// Changing the number on an existing account re-verifies the NEW number by SMS
// code before it's saved (the phone doubles as a login identity + receipt
// channel). `start` texts a code; `verify` checks it, persists the change
// (server resets the SMS opt-in) and returns a re-minted session token.

export async function startPhoneChange(args: { phone: string }): Promise<{ ok: boolean }> {
  return request('/api/portal/me/phone/start', {
    method: 'POST',
    body: JSON.stringify(args),
  }, { authed: true })
}

export async function verifyPhoneChange(
  args: { phone: string; code: string },
): Promise<{ token: string; phone: string; profile: Omit<CustomerProfile, 'visits'> }> {
  return request('/api/portal/me/phone/verify', {
    method: 'POST',
    body: JSON.stringify(args),
  }, { authed: true })
}

// ─── Plate management ────────────────────────────────────────────────────────

/**
 * Add a plate. Idempotent — re-adding the same plate updates nickname/primary.
 * Pass `expires_at` (ISO instant) to save it as a temporary plate that
 * auto-removes on that date; omit for a permanent plate.
 */
export async function addPlate(args: {
  plate: string
  nickname?: string
  is_primary?: boolean
  expires_at?: string
}): Promise<{ plate: Plate; plates: Plate[] }> {
  return request('/api/portal/plates', {
    method: 'POST',
    body: JSON.stringify(args),
  }, { authed: true })
}

/** Remove a plate by id. Server auto-promotes a new primary if needed. */
export async function removePlate(plateId: number): Promise<{ plates: Plate[] }> {
  return request(`/api/portal/plates?id=${plateId}`, {
    method: 'DELETE',
  }, { authed: true })
}

// ─── Extend an active session ─────────────────────────────────────────────────
// Adds time to a live (in-progress) parking session and charges the saved card
// off-session — no plate re-entry, no new payment sheet. Only works for
// Parkify-native sessions (visit.source === 'parkify') that saved a card.
// `idempotencyKey` must be stable for a given user action so a retry/double-tap
// can't double-charge.

export async function extendSession(args: {
  txnId: string
  additionalMinutes: number
  idempotencyKey: string
}): Promise<ExtendResult> {
  return request<ExtendResult>('/api/sessions/extend', {
    method: 'POST',
    body: JSON.stringify(args),
  }, { authed: true })
}

// ─── Quote an extension (exact price preview, no charge) ──────────────────────
// Returns the authoritative add-time price the server WOULD charge for
// `additionalMinutes` — computed from the lot's live hourly tier, daily-max, and
// fee composition, i.e. the same figure /api/sessions/extend lands on. Lets the
// confirm step show the exact amount instead of a client-side estimate. No
// idempotency key: this is a pure read and never moves money.

export async function extendQuote(args: {
  txnId: string
  additionalMinutes: number
}): Promise<ExtendQuote> {
  return request<ExtendQuote>('/api/sessions/extend/quote', {
    method: 'POST',
    body: JSON.stringify(args),
  }, { authed: true })
}

// ─── Billing (Stripe Customer Portal) ────────────────────────────────────────
//
// Creates a Stripe billing portal session and returns the launch URL. The
// browser never sees the Stripe customer id — the Worker handles binding +
// provisioning. The URL is single-use and time-limited by Stripe.

export async function createBillingSession(): Promise<{ url: string }> {
  return request('/api/portal/billing-session', { method: 'POST' }, { authed: true })
}

export async function fetchPaymentMethod(): Promise<PaymentMethod> {
  return request<PaymentMethod>('/api/portal/payment-method', { method: 'GET' }, { authed: true })
}

export async function listPaymentMethods(): Promise<{ methods: SavedCard[]; defaultId: string | null }> {
  return request('/api/portal/payment-methods', { method: 'GET' }, { authed: true })
}

export async function setDefaultPaymentMethod(paymentMethodId: string): Promise<{ ok: boolean; defaultId: string }> {
  return request('/api/portal/payment-methods/default', {
    method: 'POST', body: JSON.stringify({ paymentMethodId }),
  }, { authed: true })
}

export async function setCardLabel(paymentMethodId: string, label: string): Promise<{ ok: boolean; label: string | null }> {
  return request('/api/portal/payment-methods/label', {
    method: 'POST', body: JSON.stringify({ paymentMethodId, label }),
  }, { authed: true })
}

export async function removePaymentMethod(paymentMethodId: string): Promise<{ ok: boolean; removedId: string; defaultId: string | null }> {
  return request(`/api/portal/payment-methods?id=${encodeURIComponent(paymentMethodId)}`, {
    method: 'DELETE',
  }, { authed: true })
}

// ─── Monthly permits (Permits tab) ────────────────────────────────────────────
// Read-only view of the signed-in parker's active monthly passes + one-off
// permits. The Worker does the join (their plates → active subscription/permit
// records) and returns only display-safe fields — no Stripe ids, no raw email.

export async function fetchSubscriptions(): Promise<PortalSubscription[]> {
  return request<PortalSubscription[]>('/api/portal/subscriptions', { method: 'GET' }, { authed: true })
}

export async function fetchPermits(): Promise<PortalPermit[]> {
  return request<PortalPermit[]>('/api/portal/permits', { method: 'GET' }, { authed: true })
}

// ⚠️ Money mutation. Turn auto-renewal on/off for a SELF-MANAGED monthly pass.
// autoRenew=false leaves the pass active through the current period, then it
// lapses (no immediate loss of paid-through access). The Worker verifies the
// pass belongs to the signed-in parker and rejects company-managed seats.
// Returns the updated card so the UI reflects the new state without a refetch.
export async function setAutoRenew(id: string, autoRenew: boolean): Promise<PortalSubscription> {
  return request<PortalSubscription>(
    `/api/portal/subscriptions/${encodeURIComponent(id)}/auto-renew`,
    { method: 'POST', body: JSON.stringify({ autoRenew }) },
    { authed: true },
  )
}

// ─── Receipt download ────────────────────────────────────────────────────────
// Returns a Blob (PDF). Caller is responsible for creating an object URL and
// triggering download/open. We bypass the generic `request()` helper because
// it assumes JSON; receipts come back as application/pdf.

export async function fetchReceipt(visitId: string): Promise<Blob> {
  const token = getToken()
  if (!token) throw new ApiError(401, 'Not signed in')
  const res = await fetch(`${API_BASE}/api/portal/receipts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ visit_id: visitId }),
  })
  if (res.status === 401) {
    clearSession()
    throw new ApiError(401, 'Session expired')
  }
  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json() as { error?: string }
      if (body.error) detail = body.error
    } catch { /* keep statusText */ }
    throw new ApiError(res.status, detail)
  }
  return res.blob()
}

// ─── Public location directory (no auth) ────────────────────────────────────
// The no-QR "find parking" browse experience (slice 5) — its PublicLocation
// type + fetchLocations client now live in the shared @parkify/find-parking
// package (reused by the consumer checkout's no-zone screen), which fetches
// GET {API_BASE}/api/locations directly. Import from there, not here.

// ─── Ask Avery (authenticated customer assistant) ────────────────────────────
// The message/action/response types now live in the shared @parkify/account
// package (both AveryChat's home there and this file's own function bodies
// below reference them). Re-exported so existing portal importers that pulled
// them from here keep working.
export type { CustomerChatAction } from '@parkify/account'

// Sends the conversation so far to the authenticated assistant. `tz` is the
// browser's IANA timezone so "this month" / dates resolve to the customer's
// local calendar.
export async function sendCustomerChat(
  messages: CustomerChatMessage[],
  tz: string,
): Promise<CustomerChatResponse> {
  return request('/api/customer/chat', {
    method: 'POST',
    body: JSON.stringify({ messages, tz }),
  }, { authed: true })
}

// Emails the receipt PDF for a visit to the customer's address on file
// (Avery's "email me my receipt" confirm action).
export async function emailReceipt(visitId: string): Promise<{ ok: true }> {
  return request('/api/portal/receipts/email', {
    method: 'POST',
    body: JSON.stringify({ visit_id: visitId }),
  }, { authed: true })
}

// Files a refund REQUEST for a visit (human-reviewed — does NOT issue a refund).
// Avery's "I need a refund" confirm action.
export async function fileRefundRequest(
  visitId: string,
  reason: string,
  requestedAmountUsd?: number,
): Promise<{ ok: true; refundId: number | null; humanEta: string }> {
  return request('/api/portal/refund-request', {
    method: 'POST',
    body: JSON.stringify({
      visit_id: visitId,
      reason,
      // Partial request → include the dollar amount; the Worker re-validates it
      // against the session net server-side. Omitted ⇒ full-charge request.
      ...(typeof requestedAmountUsd === 'number' ? { amount_requested_usd: requestedAmountUsd } : {}),
    }),
  }, { authed: true })
}
